/**
 * CTA Section Component
 *
 * React implementation of the CTA (call-to-action) section renderer.
 * Supports variants: banner, booking, contact, split
 */

import { CtaData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';

// =============================================================================
// TYPES
// =============================================================================

interface CtaProps extends SectionProps<CtaData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Cta({ data, ctx, sectionId, elements }: CtaProps) {
  const variant = data.variant || 'banner';

  switch (variant) {
    case 'booking':
      return <CtaBooking data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'contact':
      return <CtaContact data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'split':
      return <CtaSplit data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <CtaBanner data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// BANNER VARIANT
// =============================================================================

function CtaBanner({ data, ctx, elements }: CtaProps) {
  const { recipe } = ctx;

  const eyebrowEl = findElement(elements, 'eyebrow');
  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  return (
    <section
      id="cta"
      className="relative py-20 lg:py-28 overflow-hidden"
      style={{
        backgroundColor: recipe.colors.primary,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      {/* Background Image */}
      {data.backgroundImage && (
        <div className="absolute inset-0">
          <img
            src={data.backgroundImage}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
        </div>
      )}

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Eyebrow */}
        {(data.eyebrow || eyebrowEl) && (
          <div className="mb-4">
            {eyebrowEl ? (
              <EditableText
                element={eyebrowEl}
                as="span"
                className="text-sm font-semibold uppercase tracking-wider text-white/70"
              />
            ) : (
              <span className="text-sm font-semibold uppercase tracking-wider text-white/70">
                {data.eyebrow}
              </span>
            )}
          </div>
        )}

        {/* Headline */}
        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h2"
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
          />
        ) : data.headline ? (
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: `${recipe.typography.headingFont}, sans-serif` }}
          >
            {data.headline}
          </h2>
        ) : null}

        {/* Subheadline */}
        {subheadlineEl ? (
          <EditableText
            element={subheadlineEl}
            as="p"
            className="text-xl text-white/80 mb-8"
          />
        ) : data.subheadline ? (
          <p className="text-xl text-white/80 mb-8">{data.subheadline}</p>
        ) : null}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {data.primaryCta && (
            <a
              href={data.primaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-transform hover:scale-105"
              style={{
                backgroundColor: 'white',
                color: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {primaryCtaTextEl ? (
                <EditableText element={primaryCtaTextEl} as="span" />
              ) : (
                data.primaryCta.text
              )}
            </a>
          )}

          {data.secondaryCta && (
            <a
              href={data.secondaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg border-2 border-white/30 text-white transition-colors hover:bg-white/10"
              style={{ borderRadius: recipe.borders.buttonRadius }}
            >
              {secondaryCtaTextEl ? (
                <EditableText element={secondaryCtaTextEl} as="span" />
              ) : (
                data.secondaryCta.text
              )}
            </a>
          )}
        </div>

        {/* Features */}
        {data.features && data.features.length > 0 && (
          <div className="flex flex-wrap justify-center gap-6 mt-10">
            {data.features.map((feature, i) => (
              <div key={i} className="flex items-center gap-2 text-white/80">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {feature}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// BOOKING VARIANT
// =============================================================================

function CtaBooking({ data, ctx, elements }: CtaProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="cta"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.surface,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="text-center p-8 md:p-12 rounded-2xl"
          style={{
            backgroundColor: recipe.colors.background,
            borderRadius: recipe.borders.cardRadius,
            boxShadow: recipe.shadows.elevated,
          }}
        >
          {/* Headline */}
          {headlineEl ? (
            <EditableText
              element={headlineEl}
              as="h2"
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            />
          ) : data.headline ? (
            <h2
              className="text-2xl md:text-3xl font-bold mb-4"
              style={{
                color: recipe.colors.text,
                fontFamily: `${recipe.typography.headingFont}, sans-serif`,
              }}
            >
              {data.headline}
            </h2>
          ) : null}

          {/* Subheadline */}
          {subheadlineEl ? (
            <EditableText
              element={subheadlineEl}
              as="p"
              className="text-lg mb-8"
              style={{ color: recipe.colors.textMuted }}
            />
          ) : data.subheadline ? (
            <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
              {data.subheadline}
            </p>
          ) : null}

          {/* Booking Widget Placeholder */}
          {data.bookingWidget ? (
            <div className="bg-gray-100 rounded-xl p-8 text-center">
              <p className="text-gray-500">
                {data.bookingWidget.provider} booking widget
              </p>
              {data.bookingWidget.url && (
                <a
                  href={data.bookingWidget.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center mt-4 px-6 py-3 font-semibold rounded-lg text-white"
                  style={{
                    backgroundColor: recipe.colors.primary,
                    borderRadius: recipe.borders.buttonRadius,
                  }}
                >
                  Book Now
                </a>
              )}
            </div>
          ) : (
            <a
              href={data.primaryCta?.url || '#contact'}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {data.primaryCta?.text || 'Book Now'}
            </a>
          )}

          {/* Contact Info */}
          {(data.phone || data.email) && (
            <div className="flex justify-center gap-6 mt-8 pt-8 border-t" style={{ borderColor: `${recipe.colors.textMuted}20` }}>
              {data.phone && (
                <a
                  href={`tel:${data.phone}`}
                  className="flex items-center gap-2 transition-colors"
                  style={{ color: recipe.colors.primary }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  {data.phone}
                </a>
              )}
              {data.email && (
                <a
                  href={`mailto:${data.email}`}
                  className="flex items-center gap-2 transition-colors"
                  style={{ color: recipe.colors.primary }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {data.email}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// CONTACT VARIANT
// =============================================================================

function CtaContact({ data, ctx, elements }: CtaProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');

  return (
    <section
      id="cta"
      className="py-20 lg:py-28"
      style={{
        backgroundColor: recipe.colors.background,
        fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-8"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            {/* Contact Methods */}
            <div className="space-y-4">
              {data.phone && (
                <a
                  href={`tel:${data.phone}`}
                  className="flex items-center gap-4 p-4 rounded-xl transition-colors"
                  style={{ backgroundColor: recipe.colors.surface }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: recipe.colors.primary }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>Call us</p>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>{data.phone}</p>
                  </div>
                </a>
              )}

              {data.email && (
                <a
                  href={`mailto:${data.email}`}
                  className="flex items-center gap-4 p-4 rounded-xl transition-colors"
                  style={{ backgroundColor: recipe.colors.surface }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: recipe.colors.primary }}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: recipe.colors.textMuted }}>Email us</p>
                    <p className="font-semibold" style={{ color: recipe.colors.text }}>{data.email}</p>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* CTA Button/Image */}
          <div className="text-center lg:text-left">
            {data.primaryCta && (
              <a
                href={data.primaryCta.url}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white"
                style={{
                  backgroundColor: recipe.colors.primary,
                  borderRadius: recipe.borders.buttonRadius,
                }}
              >
                {data.primaryCta.text}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// SPLIT VARIANT
// =============================================================================

function CtaSplit({ data, ctx, elements }: CtaProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const splitImageEl = findElement(elements, 'splitImage');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');

  return (
    <section
      id="cta"
      className="py-0 lg:py-0"
      style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
    >
      <div className="grid lg:grid-cols-2">
        {/* Image Side */}
        <div className="relative min-h-[400px] lg:min-h-[600px]">
          {splitImageEl ? (
            <EditableImage
              element={splitImageEl}
              alt={data.headline || 'CTA'}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : data.splitImage ? (
            <img
              src={data.splitImage}
              alt={data.headline || 'CTA'}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: recipe.colors.primary }}
            />
          )}
        </div>

        {/* Content Side */}
        <div
          className="flex items-center px-8 lg:px-16 py-16 lg:py-24"
          style={{ backgroundColor: recipe.colors.surface }}
        >
          <div className="max-w-lg">
            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h2"
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              />
            ) : data.headline ? (
              <h2
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  color: recipe.colors.text,
                  fontFamily: `${recipe.typography.headingFont}, sans-serif`,
                }}
              >
                {data.headline}
              </h2>
            ) : null}

            {subheadlineEl ? (
              <EditableText
                element={subheadlineEl}
                as="p"
                className="text-lg mb-8"
                style={{ color: recipe.colors.textMuted }}
              />
            ) : data.subheadline ? (
              <p className="text-lg mb-8" style={{ color: recipe.colors.textMuted }}>
                {data.subheadline}
              </p>
            ) : null}

            {/* Features */}
            {data.features && data.features.length > 0 && (
              <ul className="space-y-3 mb-8">
                {data.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      style={{ color: recipe.colors.primary }}
                    >
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span style={{ color: recipe.colors.text }}>{feature}</span>
                  </li>
                ))}
              </ul>
            )}

            {data.primaryCta && (
              <a
                href={data.primaryCta.url}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg text-white transition-transform hover:scale-105"
                style={{
                  backgroundColor: recipe.colors.primary,
                  borderRadius: recipe.borders.buttonRadius,
                }}
              >
                {primaryCtaTextEl ? (
                  <EditableText element={primaryCtaTextEl} as="span" />
                ) : (
                  data.primaryCta.text
                )}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Cta;
